import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sendPasswordResetEmail } from "firebase/auth";
import { AuthShell } from "../components/auth-shell";
import { auth } from "../firebase";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const loginHref = `/login${params.toString() ? "?" + params.toString() : ""}`;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorKey(null);
    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/invalid-email") {
        setErrorKey("auth.errors.invalidEmail");
      } else if (code === "auth/too-many-requests") {
        setErrorKey("auth.errors.tooManyAttempts");
      } else {
        // Note: Firebase intentionally doesn't differentiate "user not found"
        // from success on this endpoint, so we don't either — we just show the
        // sent screen even if the address has no account, to avoid user
        // enumeration. Genuine network or config errors land here.
        setErrorKey("forgotPassword.errors.sendFailed");
      }
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell
        formTitle={t("forgotPassword.sentTitle")}
        formSub={t("forgotPassword.sentSub", { email })}
        footer={<Link to={loginHref}>{t("forgotPassword.backToLogin")}</Link>}
      >
        <div className="acx__alert acx__alert--info" role="status">
          {t("forgotPassword.sentBody")}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      formTitle={t("forgotPassword.title")}
      formSub={t("forgotPassword.sub")}
      footer={<Link to={loginHref}>{t("forgotPassword.backToLogin")}</Link>}
    >
      <form onSubmit={submit} className="acx__form" noValidate>
        <div className="acx__field">
          <label htmlFor="acx-forgot-email" className="acx__label">{t("auth.email")}</label>
          <input
            id="acx-forgot-email"
            className="acx__input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("auth.emailPlaceholder")}
            autoComplete="email"
            disabled={busy}
          />
        </div>
        <button type="submit" className="acx__btn acx__btn--primary acx__btn--full" disabled={busy}>
          {busy ? t("forgotPassword.sending") : t("forgotPassword.cta")}
        </button>
        {errorKey && (
          <div className="acx__alert acx__alert--danger" role="alert">
            {t(errorKey)}
          </div>
        )}
      </form>
    </AuthShell>
  );
}
